<template>
  <v-card class="w-full rounded-0" flat :color="$vuetify.theme.currentTheme.navigationColor">
    <!-- Mobile Header Bar -->
    <div class="mobile-form-header flex items-center justify-between px-4 py-3 border-b sticky top-0 z-10 bg-white">
      <div>
        <h3 class="text-sm font-bold text-gray-800">
          {{ editCustomer?._id ? 'Edit Customer' : 'Add Customer' }}
        </h3>
        <p class="text-[10px] text-gray-400 mt-0.5">Step {{ step }} of 7</p>
      </div>
      <v-btn icon small @click="cancel">
        <v-icon size="20">mdi-close</v-icon>
      </v-btn>
    </div>

    <!-- Step Progress Bar -->
    <v-progress-linear
      :value="(step / 7) * 100"
      :color="$vuetify.theme.currentTheme.primary"
      height="3"
      class="mb-0"
    ></v-progress-linear>

    <div class="form-content pa-4 overflow-y-auto" style="max-height: calc(100vh - 130px)">
      <v-form ref="form" v-model="valid" lazy-validation>

        <!-- Step 1: Basic Info -->
        <div v-if="step === 1">
          <h4 class="text-xs font-bold text-gray-500 uppercase tracking-widest mb-4">
            <v-icon small class="mr-1" color="primary">mdi-account</v-icon>Basic Info
            <span class="text-red-500 ml-1 font-normal normal-case tracking-normal">(Mandatory)</span>
          </h4>
          <div class="flex flex-col gap-3">
            <v-text-field
              v-model="crn"
              label="CRN *"
              :rules="rules.crn"
              required
              hide-details
              outlined
              dense
              placeholder="ex: 1234567890"
            ></v-text-field>
            <v-autocomplete
              v-model="selectedEntity"
              label="Entity Name"
              :items="entities"
              item-text="entityName"
              :search-input.sync="searchEntity"
              item-value="_id"
              @update:search-input="debounceEntity"
              hide-no-data
              hide-details
              outlined
              placeholder="ex: ABC Corporation"
              dense
            ></v-autocomplete>
            <v-text-field v-model="firstName" label="First Name" hide-details outlined dense placeholder="ex: John"></v-text-field>
            <v-text-field v-model="middleName" label="Middle Name" hide-details outlined dense placeholder="ex: Doe"></v-text-field>
            <v-text-field v-model="lastName" label="Last Name" hide-details outlined dense placeholder="ex: Smith"></v-text-field>
            <v-text-field v-model="fatherName" label="Father's Name" hide-details outlined dense placeholder="ex: James Smith"></v-text-field>
            <v-text-field v-model="motherName" label="Mother's Name" hide-details outlined dense placeholder="ex: Mary Smith"></v-text-field>
            <v-menu v-model="menu" :close-on-content-click="false" transition="scale-transition" offset-y>
              <template v-slot:activator="{ on }">
                <v-text-field
                  label="Date of Birth"
                  placeholder="ex: 02-01-2023"
                  v-model="dateOfBirthFormat"
                  prepend-inner-icon="mdi-calendar"
                  readonly
                  clearable
                  v-on="on"
                  hide-details
                  outlined
                  dense
                ></v-text-field>
              </template>
              <v-date-picker v-model="dateOfBirth" :max="$dayjs().format('YYYY-MM-DD')" no-title scrollable />
            </v-menu>
            <v-select v-model="gender" :items="genderTypes" item-text="text" label="Gender" item-value="value" hide-details outlined placeholder="ex: Male" dense></v-select>
            <v-select v-model="maritalStatus" :items="['Single', 'Married', 'Divorced', 'Widowed']" label="Marital Status" hide-details outlined placeholder="ex: Single" dense></v-select>
            <v-text-field v-model="nationality" label="Nationality/Citizenship" hide-details outlined dense placeholder="ex: Indian"></v-text-field>
            <v-select v-model="idProofType" :items="['Passport', 'Driver License', 'SSN', 'Aadhar']" label="ID Proof Type" hide-details outlined dense></v-select>
            <v-text-field v-model="idProofNumber" label="ID Proof Number" hide-details outlined dense></v-text-field>
            <v-select v-model="relatedCategory" :items="[{ text: 'Collection Management', value: 'collection' }, { text: 'Loan Management', value: 'loan_management' }]" label="Related Category" hide-details outlined dense></v-select>
            <v-select v-model="subCase" :items="subCasesList" item-text="name" item-value="_id" label="Sub Case" hide-details outlined dense clearable></v-select>
          </div>
        </div>

        <!-- Step 2: Contact Details -->
        <div v-else-if="step === 2">
          <h4 class="text-xs font-bold text-gray-500 uppercase tracking-widest mb-4">
            <v-icon small class="mr-1" color="primary">mdi-phone</v-icon>Contact Details
            <span class="text-gray-400 ml-1 font-normal normal-case tracking-normal">(Optional)</span>
          </h4>
          <v-checkbox v-model="fillContactInfo" label="Add contact details" hide-details class="mb-4"></v-checkbox>
          <div v-if="fillContactInfo" class="flex flex-col gap-3">
            <v-select v-model="jobType" :items="jobTypes" item-text="text" item-value="value" label="Job Type" hide-details outlined dense></v-select>
            <v-text-field v-model="customerEmail" label="Email" hide-details outlined dense placeholder="ex: john@example.com"></v-text-field>
            <v-text-field v-model="customerMobile[0]" label="Phone Number" hide-details outlined dense placeholder="ex: 9876543210"></v-text-field>
            <v-text-field v-model="customerMobile[1]" label="Alternate Phone Number" hide-details outlined dense placeholder="ex: 9876543210"></v-text-field>
            <v-text-field v-model="emergencyName" label="Emergency Contact Name" hide-details outlined dense placeholder="ex: John Doe"></v-text-field>
            <v-text-field v-model="emergencyPhone" label="Emergency Contact Number" hide-details outlined dense placeholder="ex: 9876543210"></v-text-field>
            <v-text-field v-model="mailingAddress" label="Mailing Address" hide-details outlined dense placeholder="ex: 123 Main St, City, State"></v-text-field>
            <v-select v-model="preferredContactMethod" :items="['Email', 'Phone', 'SMS', 'Mail']" label="Preferred Contact Method" hide-details outlined dense></v-select>
          </div>
        </div>

        <!-- Step 3: Occupation Details -->
        <div v-else-if="step === 3">
          <h4 class="text-xs font-bold text-gray-500 uppercase tracking-widest mb-4">
            <v-icon small class="mr-1" color="primary">mdi-briefcase</v-icon>Occupation Details
            <span class="text-gray-400 ml-1 font-normal normal-case tracking-normal">(Optional)</span>
          </h4>
          <v-checkbox v-model="fillOccupationDetails" label="Add occupation details" hide-details class="mb-4"></v-checkbox>
          <div v-if="fillOccupationDetails" class="flex flex-col gap-3">
            <v-select v-model="employmentStatus" :items="['Full-time', 'Part-time', 'Self-employed', 'Unemployed', 'Retired']" label="Employment Status" hide-details outlined dense></v-select>
            <v-text-field v-model="employmentDuration" label="Employment Duration" hide-details outlined dense placeholder="e.g. 2 years 3 months"></v-text-field>
            <v-text-field v-model="previousEmployment" label="Previous Employment" hide-details outlined dense placeholder="ex: Engineer at XYZ Corp"></v-text-field>
            <v-text-field v-model="industrySector" label="Industry Sector" hide-details outlined dense placeholder="ex: Information Technology"></v-text-field>
            <v-text-field v-model="occupation" label="Occupation" hide-details outlined dense placeholder="ex: Software Developer"></v-text-field>
            <v-text-field v-model="designation" label="Designation" hide-details outlined dense placeholder="ex: Senior Developer"></v-text-field>
            <v-text-field v-model="company" label="Company" hide-details outlined dense placeholder="ex: ABC Technologies"></v-text-field>
            <v-text-field v-model="monthlyIncome" label="Monthly Income" type="number" prefix="₹" placeholder="ex: 50000" hide-details outlined dense @input="calculateYearlyIncome"></v-text-field>
            <v-text-field v-model="yearlyIncome" label="Yearly Income" type="number" prefix="₹" readonly hide-details outlined dense></v-text-field>
            <v-text-field v-model="taxPerYear" label="Tax Per Year" type="number" prefix="₹" placeholder="ex: 120000" hide-details outlined dense></v-text-field>
          </div>
        </div>

        <!-- Step 4: Financial Details -->
        <div v-else-if="step === 4">
          <h4 class="text-xs font-bold text-gray-500 uppercase tracking-widest mb-4">
            <v-icon small class="mr-1" color="primary">mdi-bank</v-icon>Financial Details
            <span class="text-gray-400 ml-1 font-normal normal-case tracking-normal">(Optional)</span>
          </h4>
          <v-checkbox v-model="fillFinancialDetails" label="Add financial details" hide-details class="mb-4"></v-checkbox>
          <div v-if="fillFinancialDetails" class="flex flex-col gap-3">
            <v-text-field v-model="bankAccountNumber" label="Bank Account Number" hide-details outlined dense placeholder="ex: 1234567890"></v-text-field>
            <v-text-field v-model="bankName" label="Bank Name" hide-details outlined dense placeholder="ex: HDFC Bank"></v-text-field>
            <v-text-field v-model="ifscCode" label="IFSC Code" hide-details outlined dense placeholder="ex: HDFC0001234"></v-text-field>
          </div>
        </div>

        <!-- Step 5: Address Details -->
        <div v-else-if="step === 5">
          <h4 class="text-xs font-bold text-gray-500 uppercase tracking-widest mb-4">
            <v-icon small class="mr-1" color="primary">mdi-map-marker</v-icon>Address Details
            <span class="text-gray-400 ml-1 font-normal normal-case tracking-normal">(Optional)</span>
          </h4>
          <div v-for="(details, index) in customerDetails" :key="index" class="border rounded-xl pa-4 mb-4 bg-gray-50">
            <div class="flex justify-between items-center mb-3">
              <p class="text-sm font-semibold text-gray-700">Address {{ index + 1 }}</p>
              <v-btn icon small color="red" @click="removeDetails(index)" v-show="customerDetails.length > 1">
                <v-icon size="18">mdi-delete</v-icon>
              </v-btn>
            </div>
            <div class="flex flex-col gap-3">
              <v-select v-model="details.typeOfAddress" :items="filterAddressTypes(index)" item-text="text" label="Address Type" item-value="value" hide-details outlined dense></v-select>
              <v-text-field v-model="details.pinCode" label="Pin Code" hide-details outlined dense placeholder="ex: 560001"></v-text-field>
              <v-text-field v-model="details.addressLine1" label="Address Line 1" hide-details outlined dense placeholder="ex: 123 Main Street"></v-text-field>
              <v-text-field v-model="details.addressLine2" label="Address Line 2" hide-details outlined dense placeholder="ex: Apt 4B, Near Station"></v-text-field>
              <v-text-field v-model="details.city" label="City" hide-details outlined dense placeholder="ex: Bangalore"></v-text-field>
              <v-text-field v-model="details.state" label="State" hide-details outlined dense placeholder="ex: Karnataka"></v-text-field>
              <v-text-field v-model="details.country" label="Country" hide-details outlined dense placeholder="ex: India"></v-text-field>
            </div>
          </div>
          <v-btn text small color="primary" @click="addCustomerDetails" v-if="showAddressTypeButton" style="text-transform: none">
            <v-icon left small>mdi-plus</v-icon> Add Address
          </v-btn>
        </div>

        <!-- Step 6: Preferences -->
        <div v-else-if="step === 6">
          <h4 class="text-xs font-bold text-gray-500 uppercase tracking-widest mb-4">
            <v-icon small class="mr-1" color="primary">mdi-tune</v-icon>Preferences
            <span class="text-gray-400 ml-1 font-normal normal-case tracking-normal">(Optional)</span>
          </h4>
          <v-checkbox v-model="fillPreferences" label="Add preferences" hide-details class="mb-4"></v-checkbox>
          <div v-if="fillPreferences" class="flex flex-col gap-3">
            <v-checkbox v-model="marketingOptIn" label="Opt-in for Marketing Communications" hide-details class="mt-1"></v-checkbox>
            <v-select v-model="preferredLanguage" :items="['English', 'Hindi', 'Spanish', 'French']" label="Preferred Language" hide-details outlined dense></v-select>
            <v-select v-model="servicePreferences" :items="['SMS Notifications', 'Email Updates', 'Phone Calls', 'Mobile App Notifications']" label="Service Preferences" multiple chips hide-details outlined dense></v-select>
          </div>
        </div>

        <!-- Step 7: Identity Details -->
        <div v-else-if="step === 7">
          <h4 class="text-xs font-bold text-gray-500 uppercase tracking-widest mb-4">
            <v-icon small class="mr-1" color="primary">mdi-card-account-details</v-icon>Identity Details
            <span class="text-amber-600 ml-1 font-normal normal-case tracking-normal">(Recommended)</span>
          </h4>
          <v-checkbox v-model="fillIdentityDetails" label="Add identity documents" hide-details class="mb-4"></v-checkbox>
          <div v-if="fillIdentityDetails">
            <div v-for="(id, index) in identityDetails" :key="index" class="border rounded-xl p-4 mb-4 bg-gray-50">
              <div class="flex justify-between items-center mb-3">
                <h4 class="text-sm font-bold text-primary">Document #{{ index + 1 }}</h4>
                <v-btn icon small color="red" @click="removeIdentityDetail(index)" v-if="identityDetails.length > 1">
                  <v-icon>mdi-delete</v-icon>
                </v-btn>
              </div>
              <div class="flex flex-col gap-3">
                <v-select
                  v-model="id.idType"
                  :items="['Aadhar', 'PAN', 'GSTIN', 'Driving License', 'Passport', 'Voter ID']"
                  label="ID Type"
                  outlined
                  dense
                  hide-details
                  :disabled="id.isVerfied"
                ></v-select>
                <div>
                  <v-text-field
                    :value="id.isVerfied ? formatMaskedId(id.idNumber) : id.idNumber"
                    :label="id.idType === 'GSTIN' ? 'GSTIN (15 Digits)' : id.idType === 'PAN' ? 'PAN (10 Digits)' : ['Aadhar', 'Aadhaar'].includes(id.idType) ? 'Aadhaar (12 Digits)' : 'ID Number'"
                    :placeholder="id.idType === 'GSTIN' ? 'ex : 27AAAPL1234C1ZV' : id.idType === 'PAN' ? 'ex : AAPCM7699P' : ['Aadhar', 'Aadhaar'].includes(id.idType) ? 'ex : 123456789012' : 'ex : 1234567890'"
                    outlined
                    dense
                    hide-details="auto"
                    :disabled="id.isVerfied"
                    @input="val => { id.idNumber = val ? val.toUpperCase() : ''; id.rawIdNumber = id.idNumber }"
                  >
                    <template v-slot:append v-if="!id.isVerfied && (id.idType === 'GSTIN' || id.idType === 'PAN' || ['Aadhar', 'Aadhaar'].includes(id.idType))">
                      <v-btn
                        x-small
                        color="primary"
                        class="normal-case font-semibold"
                        :loading="id.verifying"
                        :disabled="!id.idNumber || (id.idType === 'GSTIN' ? id.idNumber.length < 15 : id.idType === 'PAN' ? id.idNumber.length < 10 : id.idNumber.replace(/\D/g, '').length < 12)"
                        @click="verifyIdentityDoc(id)"
                      >
                        Verify
                      </v-btn>
                    </template>
                  </v-text-field>
                  <div v-if="id.isVerfied" class="text-[11px] text-green-600 font-semibold flex items-center gap-1 mt-1">
                    <v-icon x-small color="green">mdi-check-circle</v-icon>
                    Verified: {{ id.verifiedLegalName || id.nameAsperId }}
                  </div>
                </div>
                <v-text-field v-model="id.nameAsperId" label="Name as per ID" outlined dense hide-details></v-text-field>
                <v-text-field v-model="id.issuedBy" label="Issued By" outlined dense hide-details></v-text-field>
                <v-menu v-model="id.issueDateMenu" :close-on-content-click="false" transition="scale-transition" offset-y>
                  <template v-slot:activator="{ on }">
                    <v-text-field v-model="id.issueDate" label="Issue Date" prepend-inner-icon="mdi-calendar" readonly v-on="on" outlined dense hide-details></v-text-field>
                  </template>
                  <v-date-picker v-model="id.issueDate" @input="id.issueDateMenu = false" no-title />
                </v-menu>
                <v-menu v-model="id.expiryDateMenu" :close-on-content-click="false" transition="scale-transition" offset-y>
                  <template v-slot:activator="{ on }">
                    <v-text-field v-model="id.expiryDate" label="Expiry Date" prepend-inner-icon="mdi-calendar" readonly v-on="on" outlined dense hide-details></v-text-field>
                  </template>
                  <v-date-picker v-model="id.expiryDate" @input="id.expiryDateMenu = false" no-title />
                </v-menu>
              </div>
            </div>
            <v-btn text small color="primary" @click="addIdentityDetail" style="text-transform: none">
              <v-icon left small>mdi-plus</v-icon> Add Document
            </v-btn>
          </div>
        </div>

      </v-form>
    </div>

    <!-- Aadhaar OTP Dialog -->
    <v-dialog v-model="aadhaarOtpDialog" persistent max-width="360">
      <v-card class="pa-4 rounded-xl">
        <div class="flex items-center justify-between mb-3">
          <div class="flex items-center">
            <v-icon color="primary" class="mr-2" size="20">mdi-shield-lock-outline</v-icon>
            <h3 class="text-sm font-bold text-gray-800">Verify Aadhaar</h3>
          </div>
          <v-btn icon small @click="aadhaarOtpDialog = false" :disabled="aadhaarLoading">
            <v-icon size="18">mdi-close</v-icon>
          </v-btn>
        </div>
        <v-card-text class="px-0 py-2">
          <p class="text-xs text-gray-600 mb-3">
            Enter the 6-digit OTP sent to the registered mobile number.
          </p>
          <div class="flex justify-center my-3">
            <PasscodeInput
              v-if="aadhaarOtpDialog"
              v-model="aadhaarOtp"
              :otpField="6"
              :autoFocus="true"
              @keyup.enter.native="submitAadhaarOtp"
            />
          </div>
        </v-card-text>
        <v-card-actions class="px-0 pt-2">
          <v-spacer></v-spacer>
          <v-btn
            small
            color="grey darken-1"
            text
            class="normal-case font-semibold"
            :disabled="aadhaarLoading"
            @click="aadhaarOtpDialog = false"
          >
            Cancel
          </v-btn>
          <v-btn
            small
            color="primary"
            class="white--text normal-case font-semibold px-4 rounded-lg"
            :loading="aadhaarLoading"
            :disabled="!aadhaarOtp || aadhaarOtp.length !== 6"
            @click="submitAadhaarOtp"
          >
            Verify OTP
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Mobile Bottom Action Bar -->
    <div class="mobile-form-footer flex items-center justify-between px-4 py-3 border-t sticky bottom-0 bg-white z-10 gap-2">
      <v-btn v-if="step > 1" outlined small @click="step--" style="text-transform: none" class="flex-1">
        <v-icon left size="16">mdi-arrow-left</v-icon> Back
      </v-btn>
      <v-btn v-else outlined small @click="cancel" style="text-transform: none" class="flex-1">
        Cancel
      </v-btn>

      <v-btn
        v-if="step < 7"
        :color="$vuetify.theme.currentTheme.primary"
        small
        depressed
        @click="step++"
        style="text-transform: none"
        class="white--text flex-1"
      >
        Next <v-icon right size="16">mdi-arrow-right</v-icon>
      </v-btn>
      <v-btn
        v-else
        :color="$vuetify.theme.currentTheme.primary"
        small
        depressed
        @click="addCustomer"
        :loading="saving"
        :disabled="saving"
        style="text-transform: none"
        class="white--text flex-1"
      >
        <v-icon left size="16">mdi-content-save</v-icon> Save
      </v-btn>
    </div>
  </v-card>
</template>

<script>
import AddCollCustomer from './AddCollCustomer.vue'

export default {
  name: 'AddCollCustomer_mobile',
  extends: AddCollCustomer
}
</script>

<style scoped>
.mobile-form-header {
  min-height: 56px;
  background: #fff;
}
.mobile-form-footer {
  min-height: 60px;
  background: #fff;
  box-shadow: 0 -2px 8px rgba(0, 0, 0, 0.06);
}
.form-content {
  background: #f8fafc;
}
</style>
